import { toast } from "react-toastify";
import { trackEvent } from "./EventTracker";

declare global {
    interface Window {
        FB?: any;
        fbAsyncInit?: () => void;
    }
}

let loadPromise: Promise<any> | null = null;

/**
 * Resets the Facebook SDK state. For testing only.
 * @internal
 */
export function _resetForTesting() {
    loadPromise = null;
    delete window.FB;
    delete window.fbAsyncInit;
    const existingScript = document.getElementById("facebook-jssdk");
    if (existingScript) {
        existingScript.remove();
    }
}

/**
 * Dynamically loads the Facebook SDK on first use.
 * Returns a promise that resolves with the FB object when ready.
 * Subsequent calls return the same promise to prevent duplicate loading.
 */
function loadFacebookSDK(): Promise<any> {
    // Return existing promise if already loading/loaded
    if (loadPromise) return loadPromise;

    loadPromise = new Promise((resolve, reject) => {
        // Check if already loaded
        if (window.FB) {
            resolve(window.FB);
            return;
        }

        // Timeout after 5 seconds
        const timeout = setTimeout(() => {
            loadPromise = null; // Allow retry
            reject(new Error("Facebook SDK load timeout"));
        }, 5000);

        // Set up async init callback
        window.fbAsyncInit = function () {
            clearTimeout(timeout);
            window.FB.init({
                appId: 594319017256696,
                status: true,
                xfbml: true,
                version: "v18.0",
            });
            resolve(window.FB);
        };

        // Dynamically inject script
        const script = document.createElement("script");
        script.id = "facebook-jssdk";
        script.src = "https://connect.facebook.net/en_US/sdk.js";
        script.async = true;
        script.onerror = () => {
            clearTimeout(timeout);
            loadPromise = null; // Allow retry
            reject(new Error("Failed to load Facebook SDK"));
        };

        // Insert script into document
        const firstScript = document.getElementsByTagName("script")[0];
        if (firstScript?.parentNode) {
            firstScript.parentNode.insertBefore(script, firstScript);
        } else {
            document.head.appendChild(script);
        }
    });

    return loadPromise;
}

/**
 * Opens Facebook UI dialog (e.g., share dialog).
 * Lazy-loads the Facebook SDK on first call.
 */
export async function facebookUi(options: {
    method: "share";
    href: string;
}): Promise<void> {
    try {
        trackEvent("FACEBOOK_SHARE_INITIATED", { href: options.href });
        const FB = await loadFacebookSDK();
        trackEvent("FACEBOOK_SDK_LOADED", { href: options.href });
        FB.ui(options);
        trackEvent("FACEBOOK_SHARE_DIALOG_OPENED", { href: options.href });
    } catch (error) {
        console.error("Facebook sharing failed:", error);
        trackEvent("FACEBOOK_SHARE_FAILED", {
            href: options.href,
            error: error instanceof Error ? error.message : "Unknown error",
        });
        toast.error("Facebook sharing unavailable. Copy link instead.");
    }
}
